import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { Advert } from '@/core/api/entities/campaign/advert'
import type { CampaignConfigFormValues } from '@/features/campaigns/schemas/createCampaignSchema'

export type CampaignFlowMode = 'create' | 'edit'

export interface CampaignFlowState {
	/**
	 * Current flow mode. null means no flow is active.
	 */
	mode: CampaignFlowMode | null
	/**
	 * Campaign configuration from step 1.
	 */
	config: CampaignConfigFormValues | null
	/**
	 * Campaign ID when editing an existing campaign.
	 */
	campaignId?: number
	/**
	 * Account ID for the campaign.
	 */
	accountId?: number
	/**
	 * IDs of adverts associated with this campaign.
	 */
	advertIds: number[]
	/**
	 * Full advert objects for display purposes.
	 */
	adverts: Advert[]
	/**
	 * Flag to track if the flow has been initialized.
	 * Used to determine whether to fetch from API or use cached state
	 * when navigating back to step 1.
	 */
	isInitialized: boolean
}

const initialState: CampaignFlowState = {
	mode: null,
	config: null,
	campaignId: undefined,
	accountId: undefined,
	advertIds: [],
	adverts: [],
	isInitialized: false,
}

const campaignFlowSlice = createSlice({
	name: 'campaignFlow',
	initialState,
	reducers: {
		/**
		 * Initialize or update the entire campaign flow state.
		 */
		setCampaignFlow: (
			state,
			action: PayloadAction<{
				mode: CampaignFlowMode
				config: CampaignConfigFormValues
				campaignId?: number
				accountId?: number
				advertIds?: number[]
				adverts?: Advert[]
			}>
		) => {
			state.mode = action.payload.mode
			state.config = action.payload.config
			state.campaignId = action.payload.campaignId
			state.accountId = action.payload.accountId
			state.advertIds = action.payload.advertIds ?? []
			state.adverts = action.payload.adverts ?? []
			state.isInitialized = true
		},

		/**
		 * Update only the campaign configuration (step 1 form values).
		 */
		updateConfig: (state, action: PayloadAction<CampaignConfigFormValues>) => {
			state.config = action.payload
		},

		/**
		 * Update the adverts selection.
		 */
		setAdverts: (
			state,
			action: PayloadAction<{ advertIds: number[]; adverts: Advert[] }>
		) => {
			state.advertIds = action.payload.advertIds
			state.adverts = action.payload.adverts
		},

		/**
		 * Add adverts to the existing selection.
		 */
		addAdverts: (
			state,
			action: PayloadAction<{ advertIds: number[]; adverts: Advert[] }>
		) => {
			const existingIds = new Set(state.advertIds)
			const newIds = action.payload.advertIds.filter(
				(id) => !existingIds.has(id)
			)
			state.advertIds = [...state.advertIds, ...newIds]

			const existingAdvertIds = new Set(state.adverts.map((a) => a.id))
			const newAdverts = action.payload.adverts.filter(
				(a) => !existingAdvertIds.has(a.id)
			)
			state.adverts = [...state.adverts, ...newAdverts]
		},

		/**
		 * Replace a single advert in the flow with an updated version.
		 */
		updateAdvert: (state, action: PayloadAction<Advert>) => {
			state.adverts = state.adverts.map((ad) =>
				ad.id === action.payload.id ? action.payload : ad
			)
		},

		/**
		 * Clear the campaign flow state. Used when the flow is completed
		 * or abandoned.
		 */
		clearCampaignFlow: (state) => {
			state.mode = null
			state.config = null
			state.campaignId = undefined
			state.accountId = undefined
			state.advertIds = []
			state.adverts = []
			state.isInitialized = false
		},
	},
})

export const {
	setCampaignFlow,
	updateConfig,
	setAdverts,
	addAdverts,
	updateAdvert,
	clearCampaignFlow,
} = campaignFlowSlice.actions

export const campaignFlowReducer = campaignFlowSlice.reducer
